import { useState, useEffect, useRef } from 'react'
import { User } from '../types'
import { loadSchoolEmployees, loadUsers } from '../lib/apiPhpAdapter'
import { filterUsersForPicker, isExcludedFromUserLists } from '../lib/userListFilters'
import { comparePersonNames, personSearchRank, sortByPersonName } from '../utils/personNameSort'
import './UserAutocomplete.css'

interface UserAutocompleteProps {
  value: User | null
  onChange: (user: User | null) => void
  placeholder?: string
  filterRole?: string | null
  required?: boolean
  disabled?: boolean
  filterAvailableUsers?: boolean
  /** Jen zaměstnanci školy (@iss-cheb.cz, ne studenti). */
  employeesOnly?: boolean
  /** Jen zaměstnanci s účtem v intranetu (pro předání majetku). */
  requireUserAccount?: boolean
  /** Kolik položek zobrazit bez vyhledávání (výchozí: celý seznam). */
  visibleLimit?: number
  /** Max. výsledků při vyhledávání. */
  maxSearchResults?: number
}

function UserAutocomplete({
  value,
  onChange,
  placeholder = 'Začněte psát jméno, příjmení nebo email...',
  filterRole = null,
  required = false,
  disabled = false,
  filterAvailableUsers = false,
  employeesOnly = false,
  requireUserAccount = false,
  visibleLimit = 500,
  maxSearchResults = 500,
}: UserAutocompleteProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const visibleUsers = (list: User[]) => sortByPersonName(list).slice(0, visibleLimit)

  useEffect(() => {
    loadUsersList()
  }, [filterRole, filterAvailableUsers, employeesOnly, requireUserAccount])

  useEffect(() => {
    if (value) {
      setSearchTerm(`${value.name} (${value.email})`)
    } else {
      setSearchTerm('')
    }
  }, [value])

  useEffect(() => {
    if (searchTerm.length >= 1) {
      filterUsers(searchTerm)
    } else {
      const topUsers = visibleUsers(users)
      setFilteredUsers(topUsers)
      setShowDropdown(topUsers.length > 0)
    }
  }, [searchTerm, users, visibleLimit])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const intranetAccountId = (user: User): string | null => {
    if (user.user_id) return user.user_id
    if (user.id.startsWith('se-')) return null
    return user.id
  }

  const loadUsersList = async () => {
    try {
      setLoading(true)
      let allUsers = employeesOnly ? await loadSchoolEmployees() : await loadUsers()

      if (requireUserAccount) {
        allUsers = allUsers.filter((user) => intranetAccountId(user) !== null)
      }

      if (filterRole) {
        allUsers = allUsers.filter((user) => user.role === filterRole)
      }

      if (filterAvailableUsers) {
        const itAdminRoles = [
          'director',
          'financist',
          'referent',
          'referent_eko',
          'vedouci',
          'property_referent',
          'admin',
          'personalista',
          'zocmos',
          'zastupce_reditele',
        ]
        allUsers = allUsers.filter((user) => {
          const role = user.role || null
          return (role === 'requester' || role === null) && !itAdminRoles.includes(role || '')
        })
      }

      setUsers(
        sortByPersonName(
          employeesOnly
            ? allUsers.filter((u) => !isExcludedFromUserLists(u.email))
            : filterUsersForPicker(allUsers, { employeesOnly }),
        ),
      )
    } catch (error) {
      console.error('Chyba při načítání uživatelů z DB:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterUsers = (term: string) => {
    const lowerTerm = term.toLowerCase().trim()

    if (!lowerTerm) {
      const topUsers = visibleUsers(users)
      setFilteredUsers(topUsers)
      setShowDropdown(topUsers.length > 0)
      return
    }

    const searchWords = lowerTerm.split(/\s+/)

    const filtered = users.filter((user) => {
      const nameLower = user.name.toLowerCase()
      const emailLower = user.email.toLowerCase()
      const pnLower = String(user.personal_number || '').toLowerCase()
      return searchWords.every(
        (word) =>
          nameLower.includes(word) ||
          emailLower.includes(word) ||
          (employeesOnly && pnLower.includes(word)),
      )
    })

    const sorted = filtered.sort((a, b) => {
      const rankDiff = personSearchRank(a, lowerTerm) - personSearchRank(b, lowerTerm)
      if (rankDiff !== 0) return rankDiff
      const aEmailStarts = a.email.toLowerCase().startsWith(lowerTerm)
      const bEmailStarts = b.email.toLowerCase().startsWith(lowerTerm)
      if (aEmailStarts && !bEmailStarts) return -1
      if (!aEmailStarts && bEmailStarts) return 1
      return comparePersonNames(a, b)
    })

    const limited = sorted.slice(0, maxSearchResults)
    setFilteredUsers(limited)
    setShowDropdown(limited.length > 0)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value
    setSearchTerm(term)

    if (!term) {
      onChange(null)
    }
  }

  const handleSelectUser = (user: User) => {
    const accountId = intranetAccountId(user)
    const selected: User = accountId && accountId !== user.id ? { ...user, id: accountId } : user
    onChange(selected)
    const pn = user.personal_number ? ` · os. č. ${user.personal_number}` : ''
    setSearchTerm(`${user.name} (${user.email}${pn})`)
    setShowDropdown(false)
  }

  const handleInputFocus = () => {
    if (searchTerm.length >= 1) {
      filterUsers(searchTerm)
    } else {
      const topUsers = visibleUsers(users)
      setFilteredUsers(topUsers)
      setShowDropdown(topUsers.length > 0)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
    }
    if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  return (
    <div className="user-autocomplete">
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="user-autocomplete-input"
        autoComplete="off"
      />
      {showDropdown && filteredUsers.length > 0 && (
        <div ref={dropdownRef} className="user-autocomplete-dropdown">
          <div className="user-autocomplete-dropdown-hint">
            {searchTerm.trim()
              ? `${filteredUsers.length}${filteredUsers.length >= maxSearchResults ? '+' : ''} výsledků`
              : `${filteredUsers.length}${users.length > filteredUsers.length ? ` z ${users.length}` : ''} zaměstnanců — pište pro vyhledání`}
          </div>
          {filteredUsers.map((user) => (
            <div
              key={user.school_employee_id ?? user.id}
              className="user-autocomplete-item"
              onClick={() => handleSelectUser(user)}
            >
              <div className="user-autocomplete-name-row">
                <span className="user-autocomplete-name">{user.name}</span>
              </div>
              <div className="user-autocomplete-email">
                {[user.personal_number ? `os. č. ${user.personal_number}` : null, user.email || null]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          ))}
        </div>
      )}
      {loading && <div className="user-autocomplete-loading">Načítání...</div>}
    </div>
  )
}

export default UserAutocomplete
